import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Plus,
    Filter,
    Search,
    RefreshCw,
    Truck,
} from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import { FiEdit } from "react-icons/fi";
import Pagination from "../../components/Pagination";

const Supplier = () => {
    const [rawSuppliers, setRawSuppliers] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    const [page, setPage] = useState(1);
    const limit = 12; // items per page (client-side)

    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const res = await axiosInstance.get("/supplier");

            // API returns array directly
            const suppliers = Array.isArray(res.data) ? res.data : [];

            setRawSuppliers(suppliers);
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                "Failed to load suppliers. Please try again later."
            );
        } finally {
            setLoading(false);
        }
    }, []);


    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const debouncedSearch = useMemo(() => {
        let timeout;
        return (value) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => setSearchTerm(value), 250);
        };
    }, []);

    const filteredAndSorted = useMemo(() => {
        const safeSuppliers = Array.isArray(rawSuppliers) ? rawSuppliers : [];
        let list = [...safeSuppliers];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (s) =>
                    s.name.toLowerCase().includes(term) ||
                    s.mobile_number.includes(term) ||
                    s.email.toLowerCase().includes(term) ||
                    (s.gst_number && s.gst_number.toLowerCase().includes(term))
            );
        }

        if (sortField) {
            list.sort((a, b) => {
                const aVal = a[sortField];
                const bVal = b[sortField];
                let cmp = 0;

                if (typeof aVal === "string" && typeof bVal === "string") {
                    cmp = aVal.localeCompare(bVal);
                } else {
                    cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                }

                return sortDirection === "asc" ? cmp : -cmp;
            });
        }

        return list;
    }, [rawSuppliers, searchTerm, sortField, sortDirection]);

    // reset page when filters change
    useEffect(() => setPage(1), [searchTerm, sortField, sortDirection]);

    const totalItems = filteredAndSorted.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / limit));
    const startIdx = (page - 1) * limit;
    const endIdx = page * limit;
    const visible = filteredAndSorted.slice(startIdx, endIdx);

    // clamp page
    useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(totalPages);
        }
        if (page < 1) {
            setPage(1);
        }
    }, [page, totalPages]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDirection === "asc" ? "Up" : "Down";
    };

    return (
        <Authenticated>
            <div className="flex flex-col min-h-screen">
                <div className="flex-1 p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                        <div className="flex items-center space-x-3">
                            <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                <Truck className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Supplier List</h1>
                                <p className="text-sm text-gray-600">Manage your Supplier's</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Link to="/new-supplier" className="w-full sm:w-auto">
                                <button className="flex w-full sm:w-auto items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all shadow-md">
                                    <Plus size={18} />
                                    Add New Supplier
                                </button>
                            </Link>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                            {/* Search */}
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, mobile, email, GST..."
                                    className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 outline-none"
                                    defaultValue={searchTerm}
                                    onChange={(e) => debouncedSearch(e.target.value)}
                                />
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={fetchSuppliers}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
                                >
                                    <RefreshCw
                                        className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                                    />
                                    <span className="hidden md:inline">Refresh</span>
                                </button>
                                <button className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                                    <Filter className="w-4 h-4" />
                                    <span className="hidden md:inline">Filter</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="p-4 mb-6 text-red-700 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                            <span className="text-xl">Warning</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl shadow-2xl border border-teal-200 overflow-hidden">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64">
                                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
                                <p className="mt-2 text-gray-600">Loading suppliers...</p>
                            </div>
                        ) : filteredAndSorted.length === 0 ? (
                            <div className="p-10 text-center">
                                <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    No suppliers found
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    {searchTerm
                                        ? "Try adjusting your search term."
                                        : "Start by adding your first supplier."}
                                </p>
                                <Link
                                    to="/new-supplier"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add New Supplier
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full table-auto min-w-max text-md">
                                        <thead>
                                            <tr className="bg-teal-600 text-left text-white">
                                                {[
                                                    { key: "name", label: "Name" },
                                                    { key: "mobile_number", label: "Mobile Number" },
                                                    { key: "email", label: "Email" },
                                                    { key: "address", label: "Address" },
                                                    { key: "gst_number", label: "GST Number" },
                                                    { key: "gst_type", label: "GST Type" },
                                                    { key: "actions", label: "Actions" },
                                                ].map((col) => (
                                                    <th
                                                        key={col.key}
                                                        className="py-3 px-4 font-semibold cursor-pointer select-none"
                                                        onClick={() =>
                                                            col.key !== "actions" &&
                                                            col.key !== "gst_type" &&
                                                            handleSort(col.key)
                                                        }
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            {col.label}
                                                            {col.key !== "actions" &&
                                                                col.key !== "gst_type" &&
                                                                sortIcon(col.key)}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visible.map((s) => (
                                                <tr
                                                    key={s.id}
                                                    className="border-b border-teal-200 hover:bg-teal-50 transition-colors"
                                                >
                                                    <td className="py-3 px-4 font-medium text-gray-700">{s.name}</td>
                                                    <td className="py-3 px-4 text-gray-700">{s.mobile_number}</td>
                                                    <td className="py-3 px-4 text-gray-700">{s.email || "--"}</td>
                                                    <td className="py-3 px-4 text-gray-700">{s.address}</td>
                                                    <td className="py-3 px-4 text-gray-700">{s.gst_number || "-"}</td>
                                                    <td className="py-3 px-4">
                                                        <span
                                                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${s.is_interstate
                                                                ? "bg-purple-100 text-purple-800"
                                                                : "bg-blue-100 text-blue-800"
                                                                }`}
                                                        >
                                                            {s.is_interstate ? "IGST" : "CGST + SGST"}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <Link to={`/edit-supplier/${s.id}`}>
                                                            <button className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg transition-all">
                                                                <FiEdit size={20} />
                                                            </button>
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalItems > 0 && (
                                    <div className="border-t border-teal-200 px-4 py-3">
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            itemsPerPage={limit}
                                            totalItems={totalItems}
                                            onPageChange={setPage}
                                            startIndex={startIdx}
                                            endIndex={Math.min(endIdx, totalItems)}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default Supplier;